import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import ThemeToggle from './ThemeToggle';
import Logo from './Logo';
import { getHostInfo } from '../lib/hosts';
import { MONTHLY } from '../lib/pricing';

/**
 * The marketing site's top bar and footer, shared by the landing page and the public
 * legal pages (/terms, /privacy) so that those pages feel like part of the site — a
 * visitor can get back to pricing, the trial or the homepage from anywhere.
 *
 * Extracted from LandingPage.js on 11/09/2026 (Rob: "when in the new pages they need to
 * be part of the site"). Light-only, hive-* palette, exactly like the landing page.
 *
 * ⚠️ LINKS MUST WORK FROM BOTH HOSTS. The landing page lives on the apex host
 * (holidayhive.dev); the legal pages are served on the apex AND on app.holidayhive.dev
 * (the sign-up screen links to them there). A bare `#pricing` only scrolls if you are
 * already on the landing page, so section links are absolute to the marketing origin —
 * on the landing page itself that is the same document and the browser just scrolls.
 */
export const CTA_PRIMARY =
  'inline-flex items-center justify-center rounded-full bg-hive-orange font-poppins font-semibold text-white ' +
  'transition-colors hover:bg-hive-orange-hover focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-hive-orange focus-visible:ring-offset-2';

const section = (id) => `${getHostInfo().marketingOrigin}/#${id}`;

export const MarketingNav = () => (
  <nav className="mx-auto flex max-w-[1240px] items-center gap-7 px-8 py-[22px]">
    <a href={getHostInfo().marketingOrigin} className="inline-flex" data-testid="logo">
      <Logo size={32} />
    </a>
    <div className="hidden gap-6 font-poppins text-[14.5px] font-medium text-hive-muted md:flex">
      <a href={section('why')} className="text-hive-muted transition-colors hover:text-hive-navy">Why HolidayHive?</a>
      <a href={section('pricing')} className="text-hive-muted transition-colors hover:text-hive-navy">Pricing</a>
      <a href={section('reviews')} className="text-hive-muted transition-colors hover:text-hive-navy">About</a>
    </div>
    <div className="ml-auto flex items-center gap-3">
      <ThemeToggle iconOnly variant="ghost" className="text-hive-muted hover:text-hive-navy" />
      <Link to="/login">
        <Button
          variant="ghost"
          className="px-[18px] py-2.5 font-poppins text-[14.5px] font-semibold text-hive-muted hover:bg-transparent hover:text-hive-navy"
          data-testid="login-nav-btn"
        >
          Log in
        </Button>
      </Link>
      <Link to="/register">
        <Button
          className={`${CTA_PRIMARY} h-auto px-6 py-3 text-[14.5px] shadow-hive-cta`}
          data-testid="get-started-nav-btn"
        >
          Pricing &amp; free trial
        </Button>
      </Link>
    </div>
  </nav>
);

// Footer items, in two groups (LEGAL-PAGES-PLAN.md §5, 11/09/2026). Anything without a
// destination is deliberately plain text — see PAGES-NEEDED.md, which tracks the pages
// that do not exist yet.
const FOOTER_GROUPS = [
  {
    heading: 'Product',
    links: [
      { label: 'Features', href: () => section('why') },
      { label: 'Pricing', href: () => section('pricing') },
      { label: "What's new?" },
      // Back 25/10/2026 (Rob's 10A): the API's own page, now that step 1 has shipped.
      { label: 'API', to: '/developers' },
      { label: 'System status' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Terms of service', to: '/terms' },
      { label: 'Privacy policy', to: '/privacy' },
      { label: 'Acceptable use', to: '/acceptable-use' },
      { label: 'Cookies', to: '/cookies' },
      { label: 'Sub-processors', to: '/sub-processors' },
      { label: 'Tax statement', to: '/tax' },
      { label: 'Contact & company details', to: '/contact' },
    ],
  },
];

const FooterLink = ({ label, href, to }) => {
  const cls = 'transition-colors hover:text-hive-orange';
  if (to) return <Link to={to} className={cls}>{label}</Link>;
  if (href) return <a href={href()} className={cls}>{label}</a>;
  return <span className="cursor-default">{label}</span>;
};

// `legalOnly` (12/09/2026): the pre-launch page on holidayhive.co.uk shows the Legal group
// alone — its "Features"/"Pricing" anchors do not exist on that page, and Rob's rule for
// that page is no link that goes nowhere.
export const MarketingFooter = ({ legalOnly = false } = {}) => (
  <footer className="relative overflow-hidden border-t border-hive-line">
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-[140px] -right-20 h-[380px] w-[380px] rounded-full"
      style={{ background: 'rgba(247,135,31,.22)' }}
    />
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-[60px] right-40 h-[260px] w-[260px] rounded-full"
      style={{ background: 'rgba(240,192,64,.3)' }}
    />
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-[180px] right-[60px] h-[320px] w-[320px] rounded-full"
      style={{ background: 'rgba(181,123,238,.22)' }}
    />
    <div className="relative mx-auto flex max-w-[1240px] flex-col gap-5 px-8 pb-14 pt-12">
      <Logo size={30} className="self-start" />
      <div className="flex flex-col gap-3">
        {FOOTER_GROUPS.filter((group) => !legalOnly || group.heading === 'Legal').map((group) => (
          <div key={group.heading} className="flex flex-wrap items-baseline gap-x-7 gap-y-2 text-[13.5px] font-bold text-hive-muted">
            <span className="w-16 font-poppins text-[12px] font-semibold uppercase tracking-wide text-hive-navy/60">
              {group.heading}
            </span>
            {group.links.map((link) => <FooterLink key={link.label} {...link} />)}
          </div>
        ))}
      </div>
      <div className="text-[13px] text-hive-muted">
        © 2026 HolidayHive. All features, {MONTHLY}/user/month.
      </div>
    </div>
  </footer>
);
